import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutHeader as Header, LayoutHero as Hero } from '@/layouts';
import { LayoutRecentlyViewedVehicles as RecentlyViewedVehicles } from '@/layouts';
import { FeaturedVehicles } from '@/components/FeaturedVehicles';
import { InstagramReels } from '@/components/InstagramReels';
import { Footer } from '@/components/Footer';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';
import { VehicleScrollContainer } from '@/components/VehicleSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Search, Filter, SlidersHorizontal, MessageCircle, CreditCard, Shield, Car, ShoppingCart, FileText, Video, ChevronLeft, ChevronRight, Wrench, Users, Package, Settings, Gauge, Handshake, HandCoins } from 'lucide-react';
import { useAppData, useFilteredVehicles } from '@/contexts/AppDataContext';
import { lazy, Suspense } from 'react';
const ServiceModal = lazy(() => import('@/components/ServiceModal').then(m => ({ default: m.ServiceModal })));
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import SEOHead from '@/components/SEOHead';
import { generateWebsiteStructuredData, generateStoreStructuredData } from '@/lib/structuredData';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

const Index = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const contextVehicles = useFilteredVehicles(selectedStore);
  const [vehicles, setVehicles] = useState(contextVehicles);
  const [filteredVehicles, setFilteredVehicles] = useState(contextVehicles);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedFuel, setSelectedFuel] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [showFilters, setShowFilters] = useState(false);
  const { colors } = useTheme();
  
  // Get selected store
  const store = selectedStore || config.stores[0];

  const buttonColor = colors.button;
  const titleColor = colors.title;

  // Map service IDs to icons, descriptions and images
  const serviceInfo: Record<string, { icon: any; title: string; description: string; color: string; image: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', color: 'bg-blue-500', image: videoCall },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', color: 'bg-green-500', image: valuation },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', color: 'bg-purple-500', image: consignment },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', color: 'bg-teal-500', image: consortium },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', color: 'bg-orange-500', image: forwardingAgent },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', color: 'bg-blue-600', image: financing },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', color: 'bg-gray-600', image: partsAccessories },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', color: 'bg-slate-600', image: scheduleReview },
    '65dcb7238eca108d87c91dba': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', color: 'bg-red-500', image: insurance },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', color: 'bg-indigo-600', image: scheduleServices },
    '65dcb7fe8eca108d87c91dbe': { icon: FileText, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', color: 'bg-cyan-500', image: fipeTable },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', color: 'bg-indigo-500', image: testdrive },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', color: 'bg-green-600', image: directSales }
  };

  const getServiceInfo = (serviceId: string) => {
    return serviceInfo[serviceId] || { icon: MessageCircle, title: 'Serviço', description: 'Entre em contato para mais informações sobre este serviço', color: 'bg-gray-500', image: '' };
  };

  // Helper function to get service image (from config or default)
  const getServiceImage = (service: any, defaultImage: string) => {
    return service.image || defaultImage;
  };

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar', '65dceeac8eca108d87c91dc6': 'Agendar', '65dcef098eca108d87c91dc7': 'Agendar', '65dcb5e78eca108d87c91db8': 'Agendar',
      '65dcb7898eca108d87c91dbc': 'Simular',
      '65dcb7548eca108d87c91dbb': 'Avaliar', '65dcb7c58eca108d87c91dbd': 'Avaliar',
      '65dcb7238eca108d87c91dba': 'Cotar', '65dcb6528eca108d87c91db9': 'Cotar', '6628fd535bda2ae51f6fc5ff': 'Cotar',
      '65dcef468eca108d87c91dc8': 'Solicitar', '65dcb8548eca108d87c91dbf': 'Solicitar',
      '65dcb7fe8eca108d87c91dbe': 'Consultar',
    };
    return buttonTexts[serviceId] || 'Solicitar';
  };

  // Update vehicles when context changes
  useEffect(() => {
    setVehicles(contextVehicles);
    setFilteredVehicles(contextVehicles);
  }, [contextVehicles]);

  // Get unique brands for filter
  const brands = Array.from(new Set(vehicles.map(v => v.brand))).sort();
  const fuels = Array.from(new Set(vehicles.map(v => v.fuel))).sort();

  // Generate autocomplete suggestions
  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];

    const term = searchTerm.toLowerCase();
    const results: Array<{ type: string; text: string; vehicle?: any }> = [];

    // Suggest brands
    brands.forEach(brand => {
      if (brand.toLowerCase().includes(term)) {
        results.push({ type: 'brand', text: brand });
      }
    });

    // Suggest models
    const models = Array.from(new Set(vehicles.map(v => `${v.brand} ${v.model}`)));
    models.forEach(model => {
      if (model.toLowerCase().includes(term) && !results.find(s => s.text === model)) {
        results.push({ type: 'model', text: model });
      }
    });

    // Suggest specific vehicles
    vehicles.forEach(vehicle => {
      const fullText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase();
      if (fullText.includes(term) && results.length < 10) {
        const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`;
        if (!results.find(s => s.text === vehicleText)) {
          results.push({
            type: 'vehicle',
            text: vehicleText,
            vehicle: vehicle
          });
        }
      }
    });

    // NEW: Suggest vehicles that match multiple criteria from search term
    // Examples: "ford 2025", "ford territory 2025 branco", "ford preto", "territory branco", "preto 2025", "ford gasolina"
    const searchWords = term.split(/\s+/).filter(word => word.length > 0);
    if (searchWords.length > 1) {
      const matchingVehicles = vehicles.filter(vehicle => {
        // Check if vehicle matches ALL search words
        return searchWords.every(word => {
          const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear} ${vehicle.color} ${vehicle.fuel} ${vehicle.transmission}`.toLowerCase();
          return vehicleText.includes(word);
        });
      });

      // Add matching vehicles to results
      matchingVehicles.forEach(vehicle => {
        const vehicleText = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''}`;
        if (!results.find(s => s.text === vehicleText) && results.length < 10) {
          results.push({
            type: 'vehicle',
            text: vehicleText,
            vehicle: vehicle
          });
        }
      });
    }

    return results.slice(0, 8); // Limit to 8 suggestions
  }, [searchTerm, vehicles, brands]);

  // Filter vehicles based on search and filters
  useEffect(() => {
    let filtered = vehicles.filter(vehicle => {
      const matchesSearch = searchTerm === '' ||
        `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesBrand = selectedBrand === 'all' || vehicle.brand === selectedBrand;
      const matchesFuel = selectedFuel === 'all' || vehicle.fuel === selectedFuel;
      const matchesType = selectedType === 'all' ||
        (selectedType === 'zerokm' && (vehicle.isZeroKm || vehicle.mileage < 100)) ||
        (selectedType === 'seminovo' && !vehicle.isZeroKm && vehicle.mileage >= 100);
      const matchesPrice = vehicle.price >= priceRange[0] && vehicle.price <= priceRange[1];

      return matchesSearch && matchesBrand && matchesFuel && matchesType && matchesPrice;
    });

    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm, selectedBrand, selectedFuel, selectedType, priceRange]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedFuel('all');
    setSelectedType('all');
    setPriceRange([0, 1000000]);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (suggestion.type === 'vehicle' && suggestion.vehicle) {
      // Navigate to vehicle detail with slug
      const slug = generateVehicleSlug(suggestion.vehicle);
      navigate(`/estoque/${slug}`);
    } else {
      // Fill search term and apply filter
      setSearchTerm(suggestion.text);
      setShowSuggestions(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setSelectedSuggestionIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-muted/30">
      <SEOHead
        title=""
        description={`Encontre o veículo ideal na ${store.company}. Carros, motos e caminhões usados e seminovos com as melhores condições de pagamento.`}
        keywords={`veículos usados, carros seminovos, ${store.company}, automóveis, motos, caminhões, financiamento, consignação`}
        image={store.frontage || `${window.location.origin}/icon.svg`}
        structuredData={generateWebsiteStructuredData(config, selectedStore)}
      />
      
      <Header config={config} />

      <Hero config={config} />

      <RecentlyViewedVehicles config={config} />

      <FeaturedVehicles vehicles={vehicles} config={config} />

      {/* Default Services Section */}
      {store?.defaultServices && store.defaultServices.length >= 2 && (
        <section className="py-6 bg-muted/30 overflow-hidden">
          <div className="container mx-auto px-4">
            {/* Services Grid with Vertical Title */}
            <div className="grid grid-cols-1 md:grid-cols-[113px_1fr] gap-4">
              {/* Vertical Title Card - Desktop only */}
              <div className="hidden md:block">
                <Card className="border border-border/50 flex items-center justify-center p-6 h-full" style={{ backgroundColor: `${buttonColor}08` }}>
                  <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                    <h2 className="text-2xl font-bold whitespace-nowrap">
                      Serviços
                    </h2>
                  </div>
                </Card>
              </div>

              {/* Services Cards Container */}
              <div className="min-w-0">
                {/* Mobile Header - Only on mobile */}
                <div className="md:hidden mb-4">
                  <h2 className="text-xl font-bold" style={{ color: titleColor }}>
                    Serviços
                  </h2>
                </div>

                <div className={`grid grid-cols-1 gap-4 ${store.defaultServices.length === 2 ? 'md:grid-cols-2' :
                  store.defaultServices.length === 3 ? 'md:grid-cols-3' :
                    'md:grid-cols-2 lg:grid-cols-4'
                  }`}>
                  {store.defaultServices.slice(0, 4).map((serviceId: string) => {
                    const service = store.services?.find((s: any) => s.service === serviceId);
                    if (!service) return null;

                    const info = getServiceInfo(serviceId);
                    const IconComponent = info.icon;
                    const serviceImage = getServiceImage(service, info.image);

                return (
                  <Suspense fallback={null} key={serviceId}>
                    <ServiceModal service={service} serviceTitle={info.title} config={config} serviceImage={serviceImage}>
                    <button
                      type="button"
                      className="w-full text-left focus:outline-none"
                    >
                      <Card
                        className="group transition-all duration-300 border border-transparent overflow-hidden flex flex-col h-full cursor-pointer"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = buttonColor;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        {/* Image Header */}
                        {serviceImage && (
                          <div className="relative aspect-[280/100] overflow-hidden">
                            <img
                              src={serviceImage}
                              alt={info.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                            {/* Icon Overlay */}
                            <div className="absolute top-4 right-4">
                              <div className={`w-12 h-12 ${info.color} rounded-xl flex items-center justify-center backdrop-blur-sm bg-white/10`}>
                                <IconComponent className="h-6 w-6 text-white rounded-lg" />
                              </div>
                            </div>

                            {/* Title Overlay */}
                            <div className="absolute bottom-4 left-4 right-4">
                              <h3 className="text-white font-bold text-lg leading-tight drop-shadow-lg">
                                {info.title}
                              </h3>
                            </div>
                          </div>
                        )}

                        {/* Content */}
                        <CardContent className="p-4 flex flex-col flex-grow">
                          <CardDescription className="text-muted-foreground">
                            {info.description}
                          </CardDescription>
                        </CardContent>
                      </Card>
                    </button>
                  </ServiceModal>
                  </Suspense>
                );
              })}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Zero KM Section */}
      {store?.ad?.showZeroKm && vehicles.filter(v => v.isZeroKm || v.mileage === 0).length > 0 && (
        <section className="pb-6 bg-muted/30 overflow-hidden">
          <div className="container mx-auto px-4">
            {/* Section Header - Simple and Left Aligned */}
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground whitespace-nowrap">
                Zero KM
              </h2>
            </div>

            <VehicleScrollContainer
              vehicles={vehicles.filter(v => v.isZeroKm || v.mileage === 0)}
              scrollId="zerokm-scroll"
              viewAllLink="/estoque?type=zerokm"
              whatsappUrl={store.whatsapp?.ios}
              maxVehicles={5}
              moreText="veículos Zero KM"
            />
          </div>
        </section>
      )}

      {/* Seminovos Section */}
      {store?.ad?.showUsed && vehicles.filter(v => !v.isZeroKm && v.mileage > 0 && !v.armored).length > 0 && (
        <section className="pb-6 bg-muted/30 overflow-hidden">
          <div className="container mx-auto px-4">
            {/* Section Header - Simple and Left Aligned */}
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground whitespace-nowrap">
                Seminovos
              </h2>
            </div>

            <VehicleScrollContainer
              vehicles={vehicles.filter(v => !v.isZeroKm && v.mileage > 0 && !v.armored)}
              scrollId="seminovos-scroll"
              viewAllLink="/estoque?type=seminovo"
              whatsappUrl={store.whatsapp?.ios}
              maxVehicles={5}
              moreText="seminovos"
            />
          </div>
        </section>
      )}

      {/* Blindados Section */}
      {store?.ad?.showArmored && vehicles.filter(v => v.armored).length > 0 && (
        <section className="pb-6 bg-muted/30 overflow-hidden">
          <div className="container mx-auto px-4">
            {/* Section Header - Simple and Left Aligned */}
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground whitespace-nowrap">
                Blindados
              </h2>
            </div>

            <VehicleScrollContainer
              vehicles={vehicles.filter(v => v.armored)}
              scrollId="blindados-scroll"
              viewAllLink="/estoque?armored=true"
              whatsappUrl={store.whatsapp?.ios}
              maxVehicles={5}
              moreText="blindados"
            />
          </div>
        </section>
      )}

      {/* Old Vehicle Inventory Section - Remove everything below */}
      <section id="estoque-old" className="py-16 bg-background" style={{ display: 'none' }}>
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Nosso Estoque Completo
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Explore nossa seleção completa de veículos zero km e seminovos com a melhor qualidade e preços competitivos
            </p>
          </div>

          {/* Search and Filters */}
          <Card className="mb-8 shadow-card">
            <CardContent className="p-6">
              {/* Search Bar */}
              <div className="flex flex-col lg:flex-row gap-4 mb-4">
                <div className="flex-1 relative" ref={searchRef}>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                  <Input
                    placeholder="Digite marca, modelo, ano ou qualquer palavra-chave..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                    className="pl-10 h-12 text-lg"
                  />

                  {/* Autocomplete Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-[9999] max-h-[400px] overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 ${index === selectedSuggestionIndex ? 'bg-muted/50' : ''
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              {suggestion.type === 'brand' && (
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Search className="h-4 w-4 text-primary" />
                                </div>
                              )}
                              {suggestion.type === 'model' && (
                                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                                  <Car className="h-4 w-4 text-blue-500" />
                                </div>
                              )}
                              {suggestion.type === 'vehicle' && suggestion.vehicle && (
                                <img
                                  src={suggestion.vehicle.photos[0]}
                                  alt={suggestion.text}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-foreground truncate">
                                {suggestion.text}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {suggestion.type === 'brand' && 'Marca'}
                                {suggestion.type === 'model' && 'Modelo'}
                                {suggestion.type === 'vehicle' && suggestion.vehicle && (
                                  `${suggestion.vehicle.modelYear} • ${new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0
                                  }).format(suggestion.vehicle.price)}`
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 h-12 px-6"
                >
                  <SlidersHorizontal className="h-5 w-5" />
                  Filtros Avançados
                </Button>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                      <SelectTrigger>
                        <SelectValue placeholder="Marca" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas as Marcas</SelectItem>
                        {brands.map(brand => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedFuel} onValueChange={setSelectedFuel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Combustível" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {fuels.map(fuel => (
                          <SelectItem key={fuel} value={fuel}>{fuel}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedType} onValueChange={setSelectedType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {store?.ad?.showZeroKm && (
                          <SelectItem value="zerokm">Zero KM</SelectItem>
                        )}
                        {store?.ad?.showUsed && (
                          <SelectItem value="seminovo">Seminovos</SelectItem>
                        )}
                      </SelectContent>
                    </Select>

                    <Button variant="outline" onClick={clearFilters}>
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              )}

              {/* Results Count */}
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {filteredVehicles.length} veículo{filteredVehicles.length !== 1 ? 's' : ''} encontrado{filteredVehicles.length !== 1 ? 's' : ''}
                </span>
                {(searchTerm || selectedBrand !== 'all' || selectedFuel !== 'all' || selectedType !== 'all') && (
                  <span>Filtros ativos</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vehicles Grid */}
          {filteredVehicles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredVehicles.slice(0, 12).map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    whatsappUrl={store.whatsapp?.ios}
                  />
                ))}
              </div>

              {/* Ver mais botão */}
              {filteredVehicles.length > 12 && (
                <div className="text-center mt-12">
                  <Button
                    size="lg"
                    onClick={() => window.location.href = '/estoque'}
                    className="px-8 py-3 text-white"
                    style={{
                      background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                    }}
                  >
                    Ver Mais no Estoque ({filteredVehicles.length - 12} veículos)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Nenhum veículo encontrado
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Não encontramos veículos com os filtros selecionados.<br />Tente ajustar sua busca ou entre em contato conosco.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={clearFilters}>
                  Limpar Filtros
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                >
                  Falar com Vendedor
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* <InstagramReels config={config} /> */}

      <Footer config={config} />
    </div>
  );
};

export default Index;
